import { ActivityLog } from '@kalos/kalos-rpc';
import {
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@kalos/ui';
import {
  type Column,
  type ColumnDef,
  getCoreRowModel,
  type Row,
  useReactTable,
} from '@tanstack/react-table';
import { parseISO } from 'date-fns';
import { Search } from 'lucide-react';
import { type FC, useEffect, useMemo, useState } from 'react';

import { ROWS_PER_PAGE } from '../constants';
import { queryKeys } from '../hooks/react-query/constants';
import {
  type ActivityLogFilter,
  useBatchGetActivityLogQuery,
} from '../hooks/react-query/useActivityLogQuery';
import { useFieldLayout } from '../hooks/useMedia';
import { useSearchPages } from '../modules/ComponentsLibrary/AdvancedSearch/useSearchPages';
import { formatNoteAge } from '../modules/ComponentsLibrary/ServiceCall/components/Notes/noteAge';
import { ActivityLogClientService, formatRelativeDateTime } from '../tools/helpers';
import { optionListClass, OptionRow } from './FilterSheet';
import { RECORD_LIST_PREVIEW, RecordListPhone } from './RecordListPhone';

const ALL = 'all';
const SYSTEM = 'system';

const personName = (a: ActivityLog) =>
  `${a.user?.firstname ?? ''} ${a.user?.lastname ?? ''}`.trim();

const HEADER_CLASSES = 'font-semibold uppercase tracking-wider text-gray-400';

const LOADING_ROWS = Array.from({ length: 5 }, () => ActivityLog.create());

interface ActivityCardProps {
  /** Scope the log to a property, customer, job or contract. Exactly one should be provided. */
  propertyId?: number;
  customerId?: number;
  eventId?: number;
  contractId?: number;
}

export const ActivityCard: FC<ActivityCardProps> = ({
  propertyId,
  customerId,
  eventId,
  contractId,
}) => {
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState('');
  const [person, setPerson] = useState<string>(ALL);

  const scope = useMemo<ActivityLogFilter>(
    () => ({
      ...(propertyId ? { propertyId } : {}),
      ...(customerId ? { customerId } : {}),
      ...(eventId ? { eventId } : {}),
      ...(contractId ? { contractId } : {}),
    }),
    [propertyId, customerId, eventId, contractId],
  );
  const enabled = !!(propertyId || customerId || eventId || contractId);

  // Separate query: distinct people who interacted (group by user_id, no limit).
  const peopleQuery = useBatchGetActivityLogQuery({
    filter: { ...scope, withoutLimit: true, groupBy: 'user_id', withUser: true },
    enabled,
  });

  const { people, hasSystem } = useMemo(() => {
    const list: { id: number; name: string }[] = [];
    let system = false;
    for (const r of peopleQuery.data?.results ?? []) {
      if (r.userId) list.push({ id: r.userId, name: personName(r) || `User #${r.userId}` });
      else system = true;
    }
    list.sort((a, b) => a.name.localeCompare(b.name));
    return { people: list, hasSystem: system };
  }, [peopleQuery.data?.results]);

  // Main paginated query, scoped + filtered by the selected person.
  const filter = useMemo<ActivityLogFilter>(() => {
    const base: ActivityLogFilter = {
      ...scope,
      withUser: true,
      pageNumber: page,
      orderBy: 'activity_date',
      orderDir: 'DESC',
    };
    if (person === SYSTEM) {
      // fieldMask forces user_id = 0 into the WHERE (pbsql drops zero-values otherwise).
      return { ...base, userId: 0, fieldMask: ['UserId'] };
    }
    if (person !== ALL) return { ...base, userId: Number(person) };
    return base;
  }, [scope, page, person]);

  const isFieldLayout = useFieldLayout();
  const activityQuery = useBatchGetActivityLogQuery({ filter, enabled: enabled && !isFieldLayout });
  // Phones: the same request a page at a time as the list scrolls (still 25 a page).
  const pages = useSearchPages<ActivityLog>({
    queryKey: [
      queryKeys.activityLog.root,
      'pages',
      queryKeys.activityLog.getHash({ ...filter, pageNumber: undefined }),
    ],
    fetchPage: async (pageNumber) => {
      const list = await ActivityLogClientService.BatchGet(
        ActivityLog.create({ ...filter, pageNumber }),
      );
      return { results: list?.results ?? [], totalCount: list?.totalCount ?? 0 };
    },
    enabled: enabled && isFieldLayout,
  });

  useEffect(() => {
    if (activityQuery.data) setCount(activityQuery.data.totalCount);
  }, [activityQuery.data]);

  const pageCount = Math.ceil(count / ROWS_PER_PAGE);

  // Search narrows the current page (the table itself is server-paginated).
  const rows = useMemo(() => {
    const results = activityQuery.data?.results ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return results;
    return results.filter(
      (a) =>
        a.activityName.toLowerCase().includes(term) || personName(a).toLowerCase().includes(term),
    );
  }, [activityQuery.data?.results, search]);

  const columns = useMemo<ColumnDef<ActivityLog>[]>(
    () => [
      {
        accessorKey: 'activityName',
        enableSorting: false,
        header: ({ column }: { column: Column<ActivityLog> }) => (
          <DataTableColumnHeader
            column={column}
            title="Action"
            hideVisibilityToggle
            className={HEADER_CLASSES}
          />
        ),
        // `anywhere` counts in the column's min width, so a long id list or URL wraps instead of
        // widening the table past the sheet.
        cell: ({ row }: { row: Row<ActivityLog> }) => (
          <span className="block text-sm text-gray-900 [overflow-wrap:anywhere]">
            {row.original.activityName}
          </span>
        ),
      },
      {
        id: 'person',
        enableSorting: false,
        header: ({ column }: { column: Column<ActivityLog> }) => (
          <DataTableColumnHeader
            column={column}
            title="Person"
            hideVisibilityToggle
            className={HEADER_CLASSES}
          />
        ),
        cell: ({ row }: { row: Row<ActivityLog> }) => (
          <span className="text-sm text-gray-700">
            {row.original.userId ? personName(row.original) || '—' : 'System'}
          </span>
        ),
      },
      {
        accessorKey: 'activityDate',
        enableSorting: false,
        meta: { className: 'lg:whitespace-nowrap' },
        header: ({ column }: { column: Column<ActivityLog> }) => (
          <DataTableColumnHeader
            column={column}
            title="Date Occurred"
            hideVisibilityToggle
            className={HEADER_CLASSES}
          />
        ),
        cell: ({ row, column }: { row: Row<ActivityLog>; column: Column<ActivityLog> }) => (
          <span className={`text-sm text-gray-600 ${column.columnDef.meta?.className ?? ''}`}>
            {formatRelativeDateTime(row.original.activityDate)}
          </span>
        ),
      },
    ],
    [],
  );

  const loadingColumns = useMemo<ColumnDef<ActivityLog>[]>(
    () => columns.map((c) => ({ ...c, cell: () => <Skeleton className="h-4 w-full" /> })),
    [columns],
  );

  const table = useReactTable<ActivityLog>({
    data: activityQuery.isFetching ? LOADING_ROWS : rows,
    columns: activityQuery.isFetching ? loadingColumns : columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isFieldLayout) {
    // Search narrows what's loaded, as desktop's narrows the page it shows.
    const term = search.trim().toLowerCase();
    const phoneRows = term
      ? pages.rows.filter(
          (a) =>
            a.activityName.toLowerCase().includes(term) ||
            personName(a).toLowerCase().includes(term),
        )
      : pages.rows;
    const personLabel = (value: string) =>
      value === ALL
        ? 'All people'
        : value === SYSTEM
          ? 'System'
          : people.find((p) => String(p.id) === value)?.name ?? 'All people';
    const personOptions = [ALL, ...(hasSystem ? [SYSTEM] : []), ...people.map((p) => String(p.id))];
    return (
      <RecordListPhone
        title="Activity"
        preview={{
          hasMore: pages.totalCount > RECORD_LIST_PREVIEW,
          allLabel: `All activity (${pages.totalCount})`,
          onCloseAll: () => {
            setSearch('');
            setPerson(ALL);
          },
        }}
        count={pages.isPending ? undefined : `(${pages.totalCount})`}
        search={{ value: search, onChange: setSearch, placeholder: 'Search activity…' }}
        filters={{
          sheetTitle: 'Filter activity',
          values: { person },
          defaultValues: { person: ALL },
          dimensions: [
            {
              id: 'person',
              label: 'Person',
              fields: ['person'],
              summary: (v) => personLabel(v.person),
            },
          ],
          onApply: (values) => setPerson(values.person),
          renderFields: (form) => (
            <ul className={optionListClass} aria-label="Person">
              {personOptions.map((value) => (
                <OptionRow
                  key={value}
                  role="radio"
                  label={personLabel(value)}
                  selected={form.watch('person') === value}
                  onClick={() => form.setValue('person', value, { shouldDirty: true })}
                />
              ))}
            </ul>
          ),
        }}
        results={{
          itemLabel: 'activity',
          emptyTitle: 'No results.',
          rowCount: phoneRows.length,
          totalCount: pages.totalCount,
          isPending: pages.isPending,
          isFetching: pages.isFetching,
          isError: pages.isError,
          isFetchingNextPage: pages.isFetchingNextPage,
          hasNextPage: pages.hasNextPage,
          onLoadMore: () => void pages.fetchNextPage(),
          onRetry: () => void pages.refetch(),
          isFiltered: term !== '' || person !== ALL,
          onClearFilters: () => {
            setSearch('');
            setPerson(ALL);
          },
        }}
      >
        {/* As the job record's activity rows: what happened (up to three lines), then who and
            when, with a rule between rows. */}
        {phoneRows.map((activity) => (
          <li
            key={activity.id}
            className="after:bg-border relative flex flex-col gap-0.5 px-4 py-3 after:absolute after:bottom-0 after:left-4 after:right-0 after:h-px last:after:hidden"
          >
            <span className="line-clamp-3 break-words text-sm font-semibold">
              {activity.activityName}
            </span>
            <span className="text-muted-foreground truncate text-sm">
              {[
                activity.userId ? personName(activity) || '—' : 'System',
                formatNoteAge(parseISO(activity.activityDate)),
              ].join(' · ')}
            </span>
          </li>
        ))}
      </RecordListPhone>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 px-6 py-5">
        <h2 className="text-lg font-semibold text-gray-900">Activity</h2>
        {!!count && <span className="text-sm text-gray-500">({count})</span>}
      </div>

      <div className="flex flex-col gap-2 px-6 pb-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            ignoreScrolling
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search activity…"
            className="pl-9"
          />
        </div>
        <Select
          value={person}
          onValueChange={(v) => {
            setPerson(v);
            setPage(0);
          }}
        >
          <SelectTrigger className="sm:w-48">
            <SelectValue placeholder="All people" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All people</SelectItem>
            {hasSystem && <SelectItem value={SYSTEM}>System</SelectItem>}
            {people.map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="px-6 pb-4">
        <div className="max-h-[28rem] max-w-full overflow-y-auto rounded-lg border border-gray-200">
          <DataTable table={table} useMobileCard headerClassName="bg-white shadow-none" />
        </div>
      </div>

      {pageCount > 1 && !search.trim() && (
        <div className="flex justify-center px-6 pb-6">
          <DataTablePagination currentPage={page} setPage={setPage} pageCount={pageCount} />
        </div>
      )}
    </div>
  );
};
